
/**
 * Module dependencies.
 */

import { Button, Heading, Text } from 'slyk-web-libs/design-system';
import { Col, Container, Row } from 'react-components/layout';
import { color } from 'react-components/styles';
import { isProduction } from 'slyk-web-libs/core';
import { useTranslate } from 'slyk-web-libs/i18n';
import React from 'react';
import sentryClient from 'slyk-web-libs/sentry-client';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  error: Error | null | undefined;
  errorInfo: {
    componentStack: string;
  } | null | undefined;
  sentryEventId: string | undefined;
};

/**
 * `Details` styled component.
 */

const Details = styled.details`
  white-space: pre-wrap;

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h1' })`
  color: ${color('primary')};
  font-weight: 600;
  margin-bottom: 16px;
  padding-top: 32px;
`;

/**
 * `Error` styled component.
 */

const Error = styled(Text)`
  margin-bottom: 8px;
`;

/**
 * Export `DefaultErrorMessage` component.
 */

export function DefaultErrorMessage(props: Props): JSX.Element {
  const { error, errorInfo, sentryEventId } = props;
  const { translate } = useTranslate();

  return (
    <Container>
      <Row>
        <Col>
          <Title>
            {translate('defaultErrorPage.title')}
          </Title>

          {!isProduction() && (
            <>
              <Error>
                {error && error.toString()}
              </Error>

              <Details>
                <summary>
                  {translate('defaultErrorPage.details.errorStack')}
                </summary>

                {error?.stack}
              </Details>

              <Details>
                <summary>
                  {translate('defaultErrorPage.details.errorInfo')}
                </summary>

                {errorInfo?.componentStack}
              </Details>
            </>
          )}

          {sentryClient.isReportDialogToShow() && (
            <Button
              onClick={() => sentryClient.showReportDialog({ sentryEventId })}
              size={'medium'}
            >
              {translate('defaultErrorPage.buttonLabel')}
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  );
}
